import {
  type Document,
  useDeleteBoardsDocuments,
  useDeleteBoardsUserRoles,
  usePatchDocuments,
  usePatchDocumentsArchive,
  usePostBoardsUserRoles,
} from 'entities/document/api';
import { usePostPdfs } from 'entities/document/api/pdfs';
import { useTranslation } from 'react-i18next';
import type { Batch } from 'shared/api/batches/types';
import { useShowNotification } from './useShowNotification';

/**
 * Custom hook to expose actions on a document collection
 */
export const useDocumentsActions = () => {
  const { t } = useTranslation('documents');
  const showNotification = useShowNotification();

  /**
   * Hook to manage the deletion of user roles from boards.
   */
  const { mutateAsync: removeMangerFromDocumentMutation } =
    useDeleteBoardsUserRoles();

  /**
   * Hook to manage the addition of user roles to boards.
   */
  const { mutateAsync: addManagerToDocumentsMutation } =
    usePostBoardsUserRoles();

  /**
   * Hook to manage the addition of user roles to boards.
   */
  const { mutateAsync: deleteDocumentsMutation } = useDeleteBoardsDocuments();

  /**
   * Hook to manage the downloading of documents as PDFs.
   */
  const { mutateAsync: downloadDocumentsMutation } = usePostPdfs();

  /**
   * Hook to manage the archiving of documents.
   */
  const { mutateAsync: archiveMutation } = usePatchDocumentsArchive();

  /**
   * Hook to manage the finishing of documents (marking as finished).
   */
  const { mutateAsync: finishMutation } = usePatchDocuments();

  /**
   * Function to archive documents.
   * @param documents
   */
  const archive = async (documents: Document[]) => {
    if (documents.length === 0) {
      return;
    }

    await archiveMutation({ ids: documents.map(d => d.id) });
  };

  /**
   * Function to finish documents.
   * @param documents
   */
  const finish = async (documents: Document[]) => {
    if (documents.length === 0) {
      return;
    }

    await finishMutation({
      documents: documents.map(d => ({
        id: d.id,
      })),
      operation: 'mark_as_finished',
    });

    showNotification({
      message: t('documents:notification.documentsMarkedAsFinished'),
    });
  };

  /**
   * Function to remove managers from documents.
   * @param documents - The collection of documents from which managers will be removed.
   * @param emails - The emails of the managers to be removed.
   */
  const removeManagersFromDocuments = async (
    documents: Document[],
    emails: string[],
  ) => {
    if (documents.length === 0 || emails.length === 0) {
      return;
    }

    const documentIds = documents.map(doc => doc.id);

    try {
      await Promise.all(
        emails.map(email =>
          removeMangerFromDocumentMutation({
            role: 'manager',
            documentIds,
            email,
          }),
        ),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error removing managers from documents:', error);
    }

    showNotification({
      message: t('documents:notification.documentsManagersRemoved'),
    });
  };

  /**
   * Function to add managers to documents.
   * @param documents - The collection of documents to which managers will be added.
   * @param emails - The emails of the managers to be added.
   */
  const addManagersToDocuments = async (
    documents: Document[],
    emails: string[],
  ) => {
    if (documents.length === 0 || emails.length === 0) {
      return;
    }

    const documentIds = documents.map(doc => doc.id);

    try {
      await Promise.all(
        emails.map(email =>
          addManagerToDocumentsMutation({
            role: 'manager',
            documentIds,
            email,
          }),
        ),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error adding managers to documents:', error);
    }

    showNotification({
      message: t('documents:notification.documentsManagersAdded'),
    });
  };

  /**
   * Function to delete documents.
   * @param documents - The collection of documents to be deleted.
   */
  const deleteDocuments = async (documents: Document[]) => {
    if (documents.length === 0) {
      return;
    }

    const documentIds = documents.map(doc => doc.id);

    await deleteDocumentsMutation({ ids: documentIds });
  };

  /**
   * Function to download documents as a ZIP file.
   * @param documents - The collection of documents to be downloaded.
   * @param options - Optional parameters for the download, such as filename and password.
   * @returns The batch ID for the download operation.
   */
  const downloadDocuments = async (
    documents: Document[],
    options?: {
      fileName?: string;
      password?: string;
    },
  ): Promise<Batch> => {
    const documentIds = documents.map(doc => ({
      id: doc.id,
    }));

    const result = await downloadDocumentsMutation({
      documents: documentIds,
      zip: true,
      zipName: options?.fileName,
      zipPassword: options?.password,
      inline: false,
    });

    return result.data;
  };

  return {
    /**
     * Function to add managers to documents.
     * @param documents - The collection of documents to which managers will be added.
     * @param emails - The emails of the managers to be added.
     */
    removeManagersFromDocuments,

    /**
     * Function to remove managers from documents.
     * @param documents - The collection of documents from which managers will be removed.
     * @param emails - The emails of the managers to be removed.
     */
    addManagersToDocuments,

    /**
     * Function to delete documents.
     * @param documents - The collection of documents to be deleted.
     */
    deleteDocuments,

    /**
     * Function to download documents as a ZIP file.
     * @param documents - The collection of documents to be downloaded.
     * @param options - Optional parameters for the download, such as filename and password.
     */
    downloadDocuments,

    /**
     * Function to archive documents.
     * @param documents - The collection of documents to be archived.
     */
    archive,

    /**
     * Function to finish documents.
     * @param documents - The collection of documents to be finished.
     */
    finish,
  };
};
